import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { replaceEqualDeep, useQueryClient, type Query, type QueryClient } from '@tanstack/react-query'
import { getAppScrollElement } from '../utils/appScroll'
import {
  SWIPE_CHAIN, chainMove, chainStopAt, swipeRegion, type ChainMove, type ChainStop, type SwipeRegion,
} from '../utils/swipeChain'
import { SWIPE_NAV } from '../utils/swipeNavConfig'
import { isSwipeNavBlocked } from './useSwipeNavBlock'
import { selectUser, useAuthStore } from '../stores/authStore'
import { readStopMemory, readStopScroll } from '../stores/stopMemory'
import { readMineSegment } from '../stores/mineSegment'
import { useLocale } from '../i18n/context'

/**
 * ADR-106 — swipe navigation across the six top-level stops, on mobile touch.
 *
 * Runs on the app's single scrolling surface (`<main id="app-scroll">`).
 * Everything a swipe DOES on arrival is the equivalent tap's: the URL and
 * history entry of `chainMove`, then the stop's own P1 address and P4 memory
 * take over. No stop knows the gesture exists.
 *
 * Where it starts, how it decides and yields: ADR-106 Decisions 3–6 and the
 * amended 4.1 — see the helpers below; unchanged by the transition rework.
 *
 * THE TRANSITION (ADR-106, fourth owner-decisions block) — shared axis, as between sibling tabs:
 *
 *  - Only what differs between the two stops moves (`swipeRegion`): the whole
 *    page between top-level screens, the segment content below the Mine /
 *    Catalog row within the Library, the part below the sub-tab strip between
 *    Catalog sub-tabs. The shared parts stay still.
 *  - During the drag that part follows the finger for as long as it moves, on
 *    a resistance curve that slows it but never stops it, and fades with it
 *    (diagnostic 2026-09-29 §1: a hard cap read as a wall).
 *  - On commit the outgoing content becomes a clone, fixed exactly where it
 *    was, and the real content is hidden while the router renders the
 *    destination. From the frame of release the clone keeps LEAVING — a
 *    compositor transition toward a partial exit that keeps running however
 *    long the main thread spends rendering (§2: 92–97 % of the wait), and
 *    that never leaves the area empty. The navigation itself is issued one
 *    frame later, so that frame carries the exit to the compositor first.
 *  - When the destination has COMMITTED (seen in a layout effect on the
 *    location) the clone completes its exit while the real content shifts in
 *    from the other side and fades in; the two overlap. The incoming stop is
 *    never rendered before the swipe commits (Decision 6.5).
 *
 * WHY THE CLONE, AND WHY WAIT FOR THE COMMIT: this app's router renders
 * location changes inside `React.startTransition` (React Router's BrowserRouter
 * default). The destination can therefore take several frames to commit, and
 * until it does the page still holds the previous stop. The first version
 * animated the page back in on the next animation frame after `navigate()` —
 * i.e. it animated the PREVIOUS screen back into view whenever the commit was
 * late, which was the flicker on rapid swipes. Nothing here is timed against
 * the commit; it is waited for.
 *
 * RAPID SWIPES: a touch while the destination is entering jumps the transition
 * to its end (never reverses or replays it). A swipe while the destination has
 * not yet committed starts from THAT destination, drags the clone (which is
 * what is on screen), and on commit retargets the same transition — so each
 * swipe moves exactly one stop and history is what that many single swipes
 * would make.
 *
 * THE SNAPSHOT NEIGHBOUR (accepted: ADR-106, fourth owner-decisions block,
 * from ADR-107 Q-A; off with `SWIPE_NAV.snapshots`). The clone above is not thrown away after its exit:
 * it is kept as the picture of the stop just left — in the document but
 * hidden, so showing it again is cheap. A swipe back toward a neighbour whose
 * picture is still valid is a pager: from the direction lock the current
 * content and the picture move together, 1:1, no fade, no resistance; on
 * release they settle to one side or the other, starting at the flick's
 * velocity. When both the settle and the route's commit are done, the picture
 * is removed and the live stop shown in the same frame — at the same place,
 * since P4 restores the offset the picture was taken at. Nothing is rendered
 * in advance and nothing is fetched: a picture is inert, hidden DOM — no
 * React, no effects, no requests. It is used only while everything it was
 * drawn from is unchanged (`captureKey`/`stillValid`); without a valid one
 * the swipe is the shared-axis transition, unchanged.
 *
 * Smoothness: gestures live in closure variables and every visual change is a
 * direct style write, at most once per animation frame — nothing in React
 * re-renders while the finger moves.
 */
export function useSwipeNavigation(contentRef: RefObject<HTMLElement | null>): {
  /** The `touch-action` the scrolling surface must carry while the gesture is live. */
  touchAction: string | undefined
} {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobile, setMobile] = useState(() => matches(SWIPE_NAV.mobileQuery))
  useEffect(() => {
    const mq = window.matchMedia(SWIPE_NAV.mobileQuery)
    setMobile(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const stop = chainStopAt(location.pathname, location.search)
  const live = mobile && stop !== null
  // What a kept snapshot was drawn from, besides the stop's own data.
  const user = useAuthStore(selectUser)
  const { locale } = useLocale()
  const queryClient = useQueryClient()

  // The listeners are attached once; they read the current values through refs.
  const current = useRef<SwipeState>({ live, stop, navigate, user, locale, queryClient })
  current.current = { live, stop, navigate, user, locale, queryClient }

  const controller = useRef<SwipeController | null>(null)
  useEffect(() => {
    const surface = getAppScrollElement()
    if (!surface) return
    const c = attachSwipe(surface, () => contentRef.current, () => current.current)
    controller.current = c
    return () => { c.detach(); controller.current = null }
  }, [contentRef])

  // A different person, a profile update or another interface language draws
  // every stop differently: no picture of the old one may be shown.
  useEffect(() => {
    controller.current?.dropSnapshots()
  }, [user, locale])

  // The destination has committed (Layout's layout effects run after the new
  // route's): now, and only now, the incoming content can enter.
  useLayoutEffect(() => {
    controller.current?.onLocation(stop)
  }, [location.pathname, location.search]) // eslint-disable-line react-hooks/exhaustive-deps

  return { touchAction: live ? 'pan-y pinch-zoom' : undefined }
}

// ── Types ────────────────────────────────────────────────────────────────────

interface SwipeState {
  live: boolean
  stop: ChainStop | null
  navigate: (to: string, opts: { replace: boolean }) => void
  user: unknown
  locale: string
  queryClient: QueryClient
}

interface SwipeController {
  onLocation: (stop: ChainStop | null) => void
  dropSnapshots: () => void
  detach: () => void
}

/** The outgoing content's static stand-in, fixed where it was on screen. */
interface Ghost {
  frame: HTMLElement
  clone: HTMLElement
  /** Where the clone rests now, relative to where it was made (px / 0–1). */
  x: number
  opacity: number
  /** The drag offset the content had when the clone was made (px). */
  madeAt: number
}

/**
 * Everything a kept picture was drawn from. The live stop would draw the same
 * thing only while every one of these is unchanged (`stillValid`).
 */
interface SnapshotKey {
  theme: string
  viewport: string
  user: unknown
  locale: string
  /** Home's greeting follows the hour; its flame and streak follow the date. */
  clock: string
  /** Bumped by events that invalidate every picture at once (fonts loaded, …). */
  epoch: number
  /** The offset P4 will restore the stop to. */
  scroll: number
  /** The stop's remembered list state (P4). */
  memory: string
  /** The queries the stop was showing, and the exact data each held. */
  queries: (readonly [Query, unknown])[]
}

/** A stop's kept picture: detached from the document except while on screen. */
interface Snapshot {
  stop: ChainStop
  /** The region it shows — only a swipe that moves the same region may use it. */
  kind: SwipeRegion
  frame: HTMLElement
  clone: HTMLElement
  key: SnapshotKey
}

/** A committed snapshot swipe: both are settling; the live stop replaces the picture when both are done. */
interface PagerSettle {
  /** The copy of the content that left — kept as the origin's picture. */
  leaving: Ghost
  leavingKey: SnapshotKey | null
  settled: boolean
  arrived: boolean
  /** The distance the two travel: the page's width (px). */
  width: number
}

interface Transition {
  /** The stop the transition started from (the clone shows it). */
  origin: ChainStop
  /** Where it is going — retargeted by a swipe made before it arrived. */
  to: ChainStop
  /** Every stop it has been retargeted through; the router may commit one on the way. */
  via: Set<ChainStop>
  direction: 1 | -1
  ghost: Ghost
  /** Regions hidden or animated for it; all are restored when it ends. */
  touched: Set<HTMLElement>
  phase: 'pending' | 'entering'
  /** The destination committed while a gesture was dragging the clone; enter when it ends. */
  arrivedDuringDrag: boolean
  /**
   * Shared-axis: what the leaving clone needs to be kept as the origin's
   * picture once it has left. Null once retargeted: the picture is then of a
   * stop that is no longer the swipe's neighbour.
   */
  keep: { key: SnapshotKey; kind: SwipeRegion } | null
  /** Set when `ghost` is a snapshot settling in (a pager swipe), until it is replaced. */
  pager: PagerSettle | null
  /** The ghost has been kept or removed; it must not be handled twice. */
  released: boolean
}

interface Gesture {
  id: number
  x0: number
  y0: number
  target: Element
  phase: 'pending' | 'horizontal'
  dx: number
  /** +1: towards the next stop to the right (finger right-to-left); −1: back. */
  direction: 1 | -1
  /** The stop the swipe is FROM: the pending destination if one is still arriving. */
  base: ChainStop | null
  move: ChainMove | null
  /** What moves under the finger, and where it rests. */
  el: HTMLElement | null
  restX: number
  restOpacity: number
  onGhost: boolean
  /** Last values written, for the clone to continue from on commit. */
  x: number
  opacity: number
  samples: { t: number; x: number }[]
  /** A valid picture of the destination, on screen beside the content: this drag is a pager. */
  snap: Snapshot | null
  /** The distance between the two in a pager drag: the page's width (px). */
  width: number
}

// ── The gesture and the transition ───────────────────────────────────────────

function attachSwipe(
  surface: HTMLElement,
  content: () => HTMLElement | null,
  state: () => SwipeState,
): SwipeController {
  let g: Gesture | null = null
  let t: Transition | null = null
  let frame: number | null = null
  /** Style writes waiting for the next frame — two elements in a pager drag. */
  const pending = new Map<HTMLElement, { x: number; opacity: number }>()
  /** True while this code dispatches its own `pointercancel` (so its listener ignores it). */
  let dispatchingOwnCancel = false
  /** The click a browser may still send after a claimed swipe is not a tap (Decision 4.3). */
  let swallowNextClick = false

  const regionEl = (kind: SwipeRegion): HTMLElement | null =>
    kind === 'app' ? content() : surface.querySelector<HTMLElement>(`[data-swipe-region="${kind}"]`)

  // ── Style writes ──
  const place = (el: HTMLElement, x: number, opacity: number, ms = 0, easing: string = SWIPE_NAV.easing) => {
    el.style.transition = ms > 0
      ? `transform ${ms}ms ${easing}, opacity ${ms}ms ${easing}`
      : 'none'
    el.style.transform = `translate3d(${x}px, 0, 0)`
    el.style.opacity = String(opacity)
  }
  const writePending = () => {
    for (const [el, w] of pending) place(el, w.x, w.opacity)
    pending.clear()
  }
  const scheduleWrite = (el: HTMLElement, x: number, opacity: number) => {
    pending.set(el, { x, opacity })
    if (frame !== null) return
    frame = requestFrame(() => {
      frame = null
      writePending()
    })
  }
  const flushWrite = () => {
    if (frame !== null) { cancelFrame(frame); frame = null }
    writePending()
  }

  // ── Kept snapshots (ADR-107 Q-A) ──
  //
  // At most one per side of the current stop: a picture is kept only for a
  // chain neighbour of the stop on screen, and only while the swipe to it
  // would move the region it shows (`prune`).
  //
  // A kept picture stays in the document, under `content-visibility: hidden`:
  // not painted, not hit-tested, not found by find-in-page — but the style and
  // layout it was given while it was on screen as the leaving copy are kept.
  // Showing it at the direction lock is then a paint, not a style-and-layout
  // pass over hundreds of nodes. (Measured, production build at the 4–6× CPU
  // stand-in: attaching a detached copy at the lock stalled the drag's first
  // frames by up to ~230 ms.)
  const snaps = new Map<ChainStop, Snapshot>()
  /** Bumped by an event that invalidates every picture; part of each key. */
  let epoch = 0

  const dropSnapshot = (stop: ChainStop) => {
    const s = snaps.get(stop)
    if (!s) return
    snaps.delete(stop)
    s.frame.remove()
  }
  const dropSnapshots = () => {
    epoch++
    if (g?.snap) cancelPagerNow(g) // a picture being dragged is not trusted any more either
    for (const stop of [...snaps.keys()]) dropSnapshot(stop)
  }
  /** Keep only the pictures a swipe from `current` could use. */
  const prune = (current: ChainStop | null) => {
    for (const s of [...snaps.values()]) {
      if (current === null || !areNeighbours(current, s.stop) || swipeRegion(current, s.stop) !== s.kind) {
        dropSnapshot(s.stop)
      }
    }
  }

  /**
   * What the picture of `stop` is being drawn from, taken at release while the
   * stop is still on screen — or null if the picture must not be kept at all,
   * because the live stop would not come back looking like it.
   */
  const captureKey = (stop: ChainStop, region: HTMLElement): SnapshotKey | null => {
    if (!SWIPE_NAV.snapshots || prefersReducedMotion()) return null
    // Something on it is animating (a skeleton, a spinner, the goal flame):
    // a transient state, and a copy would replay the animation from its start.
    if (hasCssAnimation(region)) return null
    // P4 has not yet put it at its remembered offset (content still loading):
    // the live stop would come back somewhere else.
    const remembered = readStopScroll(stop) ?? 0
    if (Math.abs(remembered - surface.scrollTop) > 1) return null
    // Explore applies its search after a pause: typed but not yet applied, it
    // completes the apply on return and shows a different list (P4 third block).
    const explore = readStopMemory().catalogExplore
    if (stop === 'catalog-explore' && explore && explore.searchInput !== explore.search) return null
    const qc = state().queryClient
    return {
      ...environmentKey(surface, state(), epoch),
      scroll: remembered,
      memory: memoryKey(stop),
      // Every query the stop observes, except a disabled one with no data:
      // nothing on screen was drawn from it (Catalog observes the sub-tab it
      // is not showing, disabled until it is shown).
      queries: qc.getQueryCache().getAll()
        .filter((q) => q.getObserversCount() > 0 && !(q.isDisabled() && q.state.data === undefined))
        .map((q) => [q, q.state.data] as const),
    }
  }

  /** Whether the live stop would still draw exactly what `s` shows. */
  const stillValid = (s: Snapshot): boolean => {
    const k = s.key
    const env = environmentKey(surface, state(), epoch)
    if (env.theme !== k.theme || env.viewport !== k.viewport || env.user !== k.user
      || env.locale !== k.locale || env.clock !== k.clock || env.epoch !== k.epoch) return false
    if ((readStopScroll(s.stop) ?? 0) !== k.scroll) return false
    if (memoryKey(s.stop) !== k.memory) return false
    // Only a real change in the data discards the picture. A query fetched
    // again with the same answer keeps its data object (structural sharing);
    // one that was evicted and fetched again (by the stop itself or by the
    // neighbour prefetch) is a new object, so its data is compared by value.
    const cache = state().queryClient.getQueryCache()
    return k.queries.every(([q, data]) => {
      const now = cache.get(q.queryHash)
      if (!now || now.state.isInvalidated || now.state.data === undefined) return false
      return now.state.data === data || replaceEqualDeep(data, now.state.data) === data
    })
  }

  /** The picture a swipe from `from` toward `move` may use right now, discarding one that is no longer valid. */
  const usableSnapshot = (from: ChainStop, move: ChainMove): Snapshot | null => {
    const s = snaps.get(move.to)
    if (!s || !SWIPE_NAV.snapshots || prefersReducedMotion()) return null
    if (s.kind !== swipeRegion(from, move.to) || !areNeighbours(from, move.to) || !stillValid(s)) {
      dropSnapshot(move.to)
      return null
    }
    return s
  }

  /** Keeps a copy that has finished leaving as `stop`'s picture, at rest and hidden. */
  const keepSnapshot = (stop: ChainStop, ghost: Ghost, key: SnapshotKey, kind: SwipeRegion) => {
    const f = ghost.frame
    f.style.setProperty('content-visibility', 'hidden')
    delete f.dataset.swipeGhost // no longer a copy in flight…
    f.dataset.swipePicture = '' // …but a kept picture
    const c = ghost.clone
    c.style.left = `${parseFloat(c.style.left || '0') - ghost.madeAt}px`
    c.style.transition = 'none'
    c.style.transform = ''
    c.style.opacity = '1'
    dropSnapshot(stop)
    snaps.set(stop, { stop, kind, frame: ghost.frame, clone: c, key })
    prune(state().stop)
  }

  // Events after which no picture can be trusted: the theme repaints every
  // stop; a new viewport size or orientation lays it out again; a web font
  // that has just loaded (the lazily loaded Cyrillic subset) re-wraps text;
  // reduced motion turns pictures off.
  const themeObserver = typeof MutationObserver === 'function' ? new MutationObserver(dropSnapshots) : null
  themeObserver?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
  window.addEventListener('resize', dropSnapshots)
  window.addEventListener('orientationchange', dropSnapshots)
  const fonts = typeof document !== 'undefined' ? (document as Document & { fonts?: EventTarget }).fonts : undefined
  fonts?.addEventListener?.('loadingdone', dropSnapshots)
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')
  reducedMotion?.addEventListener?.('change', dropSnapshots)

  // ── The pager drag: the content and the picture, 1:1 ──

  /** A pager's spring-back in flight, ended at once by the next touch. */
  let springing: { region: HTMLElement; snap: Snapshot; done: () => void } | null = null

  const showSnapshot = (s: Snapshot, x: number) => {
    s.frame.dataset.swipeSnapshot = ''
    place(s.clone, x, 1)
    s.frame.style.removeProperty('content-visibility')
    if (!s.frame.isConnected) document.body.appendChild(s.frame)
  }
  const hideSnapshot = (s: Snapshot) => {
    s.frame.style.setProperty('content-visibility', 'hidden')
    delete s.frame.dataset.swipeSnapshot
    s.clone.style.transition = 'none'
    s.clone.style.transform = ''
  }

  /** Where the content sits for finger travel `dx`: with the finger, never past rest the other way, never past a page. */
  const pagerX = (gesture: Gesture, dx: number) =>
    gesture.direction === 1 ? Math.max(-gesture.width, Math.min(0, dx)) : Math.min(gesture.width, Math.max(0, dx))

  /** Cancel, animated from the release velocity: both return to where they started. */
  const cancelPager = (gesture: Gesture) => {
    flushWrite()
    const region = gesture.el!
    const snap = gesture.snap!
    const toward = -Math.sign(gesture.x) * signedVelocity(gesture.samples) // speed back toward rest
    const { ms, easing } = settleMotion(Math.abs(gesture.x), toward)
    place(region, 0, 1, ms, easing)
    place(snap.clone, gesture.direction * gesture.width, 1, ms, easing)
    let over = false
    const done = () => {
      if (over) return
      over = true
      springing = null
      if (!t?.touched.has(region) && g?.el !== region) clearStyles(region)
      hideSnapshot(snap)
    }
    springing = { region, snap, done }
    onTransformEnd(snap.clone, ms, done)
  }
  /** Cancel with no animation: a second finger, a lost gesture, an invalidating event. */
  const cancelPagerNow = (gesture: Gesture) => {
    if (gesture.el) clearStyles(gesture.el)
    if (gesture.snap) hideSnapshot(gesture.snap)
    gesture.snap = null
  }

  /** Commit: the content leaves as a copy (the origin's next picture) while the picture settles in. */
  const commitPager = (gesture: Gesture, move: ChainMove) => {
    flushWrite()
    const region = gesture.el!
    const snap = gesture.snap!
    const origin = gesture.base!
    const leavingKey = captureKey(origin, region)
    const leaving = makeGhost(region, surface, 1, gesture.x)
    place(region, 0, 0) // hidden: the picture shows the destination until the live stop replaces it
    snaps.delete(move.to) // in use: it is on screen now, and the live stop will replace it
    const toward = -gesture.direction * signedVelocity(gesture.samples) // speed toward the destination
    const { ms, easing } = settleMotion(gesture.width - Math.abs(gesture.x), toward)
    void getComputedStyle(leaving.clone).opacity // a start state for the new clone's transition
    place(leaving.clone, -gesture.direction * gesture.width - gesture.x, 1, ms, easing)
    place(snap.clone, 0, 1, ms, easing)
    const tr: Transition = {
      origin, to: move.to, via: new Set(), direction: gesture.direction,
      ghost: { frame: snap.frame, clone: snap.clone, x: 0, opacity: 1, madeAt: 0 },
      touched: new Set([region]), phase: 'pending', arrivedDuringDrag: false,
      keep: null, pager: { leaving, leavingKey, settled: false, arrived: false, width: gesture.width }, released: false,
    }
    t = tr
    onTransformEnd(snap.clone, ms, () => {
      if (t !== tr || !tr.pager || tr.pager.settled) return
      tr.pager.settled = true
      if (tr.pager.arrived) swap(tr)
    })
    queueNavigate(move)
  }

  /** A pager settle cut short by a touch: both at their final places at once. */
  const settleNow = (tr: Transition) => {
    const p = tr.pager!
    place(p.leaving.clone, -tr.direction * p.width - p.leaving.madeAt, 1)
    place(tr.ghost.clone, 0, 1)
    p.settled = true
    if (p.arrived) swap(tr)
  }

  /** Both settled and committed: the live stop takes the picture's place, in one frame. */
  const swap = (tr: Transition) => {
    for (const el of tr.touched) clearStyles(el)
    tr.ghost.frame.remove()
    tr.released = true
    const p = tr.pager!
    if (p.leavingKey) keepSnapshot(tr.origin, p.leaving, p.leavingKey, swipeRegion(tr.origin, tr.to))
    else p.leaving.frame.remove()
    if (t === tr) t = null
    prune(state().stop)
  }

  /** The destination has committed: the shared-axis entry, or the pager's swap once it has settled. */
  const arrive = (tr: Transition) => {
    if (!tr.pager) { enter(tr); return }
    tr.pager.arrived = true
    if (tr.pager.settled) swap(tr)
  }

  /** The shared-axis clone has left (or was cut short): keep it as the origin's picture, or drop it. */
  const releaseGhost = (tr: Transition) => {
    if (tr.released) return
    tr.released = true
    if (tr.keep && tr.via.size === 0) keepSnapshot(tr.origin, tr.ghost, tr.keep.key, tr.keep.kind)
    else tr.ghost.frame.remove()
  }
  const onTransitionEnd = (el: HTMLElement, done: () => void) => {
    const handler = (e: TransitionEvent) => {
      if (e.target !== el || e.propertyName !== 'opacity') return
      el.removeEventListener('transitionend', handler)
      el.removeEventListener('transitioncancel', handler)
      done()
    }
    el.addEventListener('transitionend', handler)
    el.addEventListener('transitioncancel', handler)
  }

  // ── Navigation, one frame after the exit starts ──
  //
  // The outgoing clone's exit is a CSS transition, which the compositor runs
  // on its own once a frame has carried it there. The destination's render is
  // main-thread work (diagnostic 2026-09-29 §2: 92–97 % of the wait). If the
  // router's render ran before that frame, the exit would not start until the
  // render had finished — the frozen screen measured on the device. So the
  // navigation is issued from the NEXT animation frame: that frame paints the
  // exit's start, and from then on it keeps moving however long the render
  // takes. This orders two things; it adds no delay beyond that one frame.
  // Several swipes released within one frame are navigated in the order they
  // were made, so a burst's history is exactly that of single swipes.
  let navQueue: ChainMove[] = []
  let navFrame: number | null = null
  const queueNavigate = (move: ChainMove) => {
    navQueue.push(move)
    if (navFrame !== null) return
    // Two frame callbacks, not one: a frame callback runs BEFORE its frame is
    // drawn, so work started in the first one still precedes the frame that
    // hands the exit to the compositor. The second runs after that frame.
    // (Measured in Chromium: blocking the main thread from the first callback
    // left a transition unpainted for the whole block; from the second, it
    // painted ~60 frames a second throughout.)
    navFrame = requestFrame(() => {
      navFrame = requestFrame(() => {
        navFrame = null
        const moves = navQueue
        navQueue = []
        for (const m of moves) state().navigate(m.url, { replace: m.replace })
      })
    })
  }

  // ── Transition lifecycle ──

  /**
   * From release until the destination has rendered, the clone keeps leaving:
   * on in the swipe's direction and fading, toward a PARTIAL exit, decelerating
   * so it is still moving late. It never leaves completely, so the area is
   * never empty; if the render outlasts it, it rests there, still visible.
   */
  const startPendingExit = (tr: Transition) => {
    const toX = tr.ghost.x - tr.direction * SWIPE_NAV.pendingExitShiftPx
    const toOpacity = Math.min(tr.ghost.opacity, SWIPE_NAV.pendingExitOpacity)
    // A CSS transition needs a computed "before" style to start from. The
    // clone was inserted in this same task and has never been styled, so
    // without this read it would jump straight to the partial state and stand
    // still there (measured: one painted frame for the whole wait). Same
    // reason as the read in `enter()`.
    void getComputedStyle(tr.ghost.clone).opacity
    place(tr.ghost.clone, toX, toOpacity, SWIPE_NAV.pendingExitMs, SWIPE_NAV.pendingExitEasing)
    tr.ghost.x = toX
    tr.ghost.opacity = toOpacity
  }

  /** End a transition at its final state at once: nothing reverses, nothing replays. */
  const finish = (tr: Transition) => {
    if (tr.pager) { swap(tr); return }
    releaseGhost(tr)
    for (const el of tr.touched) clearStyles(el)
    if (t === tr) t = null
  }

  /** The destination has committed: the clone leaves and the real content arrives, together. */
  const enter = (tr: Transition) => {
    const region = regionEl(swipeRegion(tr.origin, tr.to))
    // Anything hidden for this transition that is not the part now entering
    // (a Library transition retargeted out of the Library, say) comes back.
    for (const el of tr.touched) if (el !== region) clearStyles(el)
    if (!region || prefersReducedMotion()) { finish(tr); return }
    tr.touched.add(region)
    tr.phase = 'entering'

    // Incoming: from the side it lies on, faded out, to rest — in one motion.
    place(region, tr.direction * SWIPE_NAV.enterShiftPx, 0)
    void region.getBoundingClientRect() // the start state, committed before the transition is set
    place(region, 0, 1, SWIPE_NAV.enterMs)
    onTransitionEnd(region, () => { if (t === tr) finish(tr) })

    // Outgoing: completes its exit — on in the swipe's direction while fading
    // out, overlapping the entry. A CSS transition set mid-way continues from
    // wherever the pending exit has got to, so there is no jump.
    const { clone } = tr.ghost
    place(clone, tr.ghost.x - tr.direction * SWIPE_NAV.exitShiftPx, 0, SWIPE_NAV.exitMs)
    onTransitionEnd(clone, () => releaseGhost(tr))
  }

  const onLocation = (stop: ChainStop | null) => {
    // Only pictures of this stop's neighbours, of the region a swipe to them
    // would move, are worth keeping (a tap elsewhere, leaving the chain).
    prune(stop)
    const tr = t
    if (!tr) return
    if (tr.phase === 'entering') { finish(tr); return } // navigated on (a tap): end it
    if (stop === tr.to) {
      if (g?.onGhost) tr.arrivedDuringDrag = true // enter once the finger lets go
      else arrive(tr)
    } else if (stop === null || (stop !== tr.origin && !tr.via.has(stop))) {
      finish(tr) // went somewhere else altogether
    }
    // (A stop the transition passed through on its way committing first is
    // simply a step on the way: keep waiting for the destination.)
  }

  const abandon = () => {
    const gesture = g
    g = null
    if (gesture?.phase !== 'horizontal' || !gesture.el) return
    springBack(gesture)
  }

  const springBack = (gesture: Gesture) => {
    if (gesture.snap) { cancelPager(gesture); return }
    flushWrite()
    const el = gesture.el!
    if (prefersReducedMotion()) {
      if (!gesture.onGhost) clearStyles(el)
    } else if (gesture.onGhost) {
      place(el, gesture.restX, gesture.restOpacity, SWIPE_NAV.springBackMs)
    } else {
      place(el, 0, 1, SWIPE_NAV.springBackMs)
      onTransitionEnd(el, () => { if (!t?.touched.has(el) && g?.el !== el) clearStyles(el) })
    }
    if (gesture.onGhost && t) {
      t.ghost.x = gesture.restX
      t.ghost.opacity = gesture.restOpacity
      if (t.arrivedDuringDrag) { t.arrivedDuringDrag = false; arrive(t) }
    }
  }

  // ── Pointer events ──

  const onPointerDown = (e: PointerEvent) => {
    swallowNextClick = false
    if (g && e.pointerId !== g.id) {
      abandon()
      // A second finger ends the gesture and starts nothing (Decision 6.2). A
      // new primary touch means the old one is over (its release was lost),
      // so it is armed as usual.
      if (!e.isPrimary) return
    }
    // A touch while the destination is entering ends the transition at its
    // final state — before any check, so nothing of the previous screen stays.
    // The same for a pager's settle or spring-back: no reversal, no replay.
    springing?.done()
    if (t?.pager && !t.pager.settled) settleNow(t)
    if (t?.phase === 'entering') finish(t)

    const { live, stop } = state()
    if (!live || e.pointerType !== 'touch' || !e.isPrimary) return
    const band = SWIPE_NAV.edgeBandPx
    if (e.clientX < band || e.clientX > window.innerWidth - band) return
    if (isSwipeNavBlocked()) return
    const target = e.target as Element | null
    if (!target || isEditable(target) || editableHasFocus()) return
    if (optsOut(target, surface)) return
    const onGhost = t?.phase === 'pending'
    g = {
      id: e.pointerId, x0: e.clientX, y0: e.clientY, target, phase: 'pending', dx: 0, direction: 1,
      base: onGhost ? t!.to : stop, move: null, el: null, restX: 0, restOpacity: 1, onGhost,
      x: 0, opacity: 1, samples: [{ t: e.timeStamp, x: e.clientX }], snap: null, width: 0,
    }
  }

  const onPointerMove = (e: PointerEvent) => {
    if (!g || e.pointerId !== g.id) return
    const dx = e.clientX - g.x0
    const dy = e.clientY - g.y0
    g.dx = dx
    g.samples.push({ t: e.timeStamp, x: e.clientX })
    if (g.samples.length > 8) g.samples.shift()

    if (g.phase === 'pending') {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_NAV.lockDistancePx) return
      const horizontal = Math.abs(dx) >= SWIPE_NAV.lockRatio * Math.abs(dy)
      // Released for good: vertical or diagonal belongs to the page's scroll,
      // and an inner scroller that can move this way takes the gesture.
      if (!horizontal || innerScrollerWins(g.target, dx, surface) || isSwipeNavBlocked()) { g = null; return }
      g.phase = 'horizontal'
      g.direction = dx < 0 ? 1 : -1
      g.move = g.base ? chainMove(g.base, g.direction) : null
      if (g.onGhost && t) {
        // The clone is mid-exit: take it from where it IS on screen, not from
        // where its transition is heading, so grabbing it does not jump.
        g.el = t.ghost.clone
        const now = animatedPlacement(t.ghost.clone)
        g.restX = now.x ?? t.ghost.x
        g.restOpacity = now.opacity ?? t.ghost.opacity
      } else if (g.base) {
        g.el = regionEl(swipeRegion(g.base, g.move?.to ?? null))
        // A valid picture of the neighbour this way: the drag is a pager.
        const s = g.el && g.move && t === null ? usableSnapshot(g.base, g.move) : null
        if (s) {
          g.snap = s
          g.width = pageWidth(surface)
          showSnapshot(s, g.direction * g.width)
        }
      }
      g.x = g.restX
      g.opacity = g.restOpacity
      swallowNextClick = true
      // The page has taken the gesture: tell what it started on, the way a
      // browser does when it takes over a pan — this releases a pending
      // long-press (Mine's select mode, Decision 4.2) or any press handler.
      dispatchingOwnCancel = true
      try {
        g.target.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true, pointerId: g.id, pointerType: 'touch' }))
      } finally {
        dispatchingOwnCancel = false
      }
    }

    if (!g.el || prefersReducedMotion()) return // no follow animation with reduced motion (Decision 6.5)
    if (g.snap) {
      // A pager: the content and the picture beside it move with the finger,
      // 1:1 — no resistance, no fade.
      g.x = pagerX(g, dx)
      g.opacity = 1
      scheduleWrite(g.el, g.x, 1)
      scheduleWrite(g.snap.clone, g.direction * g.width + g.x, 1)
      return
    }
    // Follow the finger for as long as it moves, with resistance that grows
    // but never becomes a stop; fade with it, never freezing. At a chain end
    // the same curves, under the ends' stronger resistance.
    const gain = g.move ? SWIPE_NAV.dragGain : SWIPE_NAV.endGain
    const travel = Math.abs(dx) * gain
    const shift = Math.sign(dx) * SWIPE_NAV.dragResistancePx * Math.log1p(travel / SWIPE_NAV.dragResistancePx)
    const fade = travel / (travel + SWIPE_NAV.dragFadeScalePx)
    g.x = g.restX + shift
    g.opacity = g.restOpacity * (1 - (1 - SWIPE_NAV.dragMinOpacity) * fade)
    scheduleWrite(g.el, g.x, g.opacity)
  }

  const onPointerUp = (e: PointerEvent) => {
    if (!g || e.pointerId !== g.id) return
    const gesture = g
    if (gesture.phase !== 'horizontal') { g = null; return }

    const travel = Math.abs(gesture.dx)
    const stillThatWay = Math.sign(gesture.dx) === -gesture.direction
    const fast = velocity(gesture.samples) >= SWIPE_NAV.commitVelocity && travel >= SWIPE_NAV.commitVelocityMinPx
    const far = travel >= SWIPE_NAV.commitFraction * window.innerWidth
    const move = gesture.move
    if (!move || !stillThatWay || !(far || fast) || isSwipeNavBlocked()) {
      g = gesture
      abandon()
      return
    }
    g = null
    flushWrite()
    if (gesture.snap) commitPager(gesture, move)
    else commit(gesture, move)
  }

  const commit = (gesture: Gesture, move: ChainMove) => {
    if (prefersReducedMotion() || !gesture.el) {
      // Instant: no shift, no fade. Anything in flight ends where it would.
      if (t) finish(t)
      if (gesture.el && !gesture.onGhost) clearStyles(gesture.el)
      queueNavigate(move)
      return
    }
    if (gesture.onGhost && t) {
      // A swipe made before the previous one arrived: the same transition now
      // goes one stop further, and the clone resumes leaving from where the
      // finger let it go.
      t.ghost.x = gesture.x
      t.ghost.opacity = gesture.opacity
      // Retargeted: the picture on screen is now what leaves, as in the
      // shared axis, and no copy made on the way is kept (not a neighbour).
      if (t.pager) { t.pager.leaving.frame.remove(); t.pager = null }
      t.keep = null
      t.via.add(t.to)
      t.to = move.to
      t.direction = gesture.direction
      t.arrivedDuringDrag = false
      startPendingExit(t)
      queueNavigate(move)
      return
    }
    const region = gesture.el
    // What the copy is drawn from, while the stop is still on screen: the copy
    // becomes this stop's picture once it has left (if it may be kept at all).
    const key = captureKey(gesture.base!, region)
    // Placed where the region is now, drag offset included, so its own offset starts at 0.
    const ghost = makeGhost(region, surface, gesture.opacity, gesture.x)
    place(region, 0, 0) // hidden: the clone shows it until the destination has arrived
    t = {
      origin: gesture.base!, to: move.to, via: new Set(), direction: gesture.direction, ghost,
      touched: new Set([region]), phase: 'pending', arrivedDuringDrag: false,
      keep: key ? { key, kind: swipeRegion(gesture.base!, move.to) } : null, pager: null, released: false,
    }
    // The exit is running from this very frame; the render starts after it.
    startPendingExit(t)
    queueNavigate(move)
  }

  const onPointerCancel = (e: PointerEvent) => {
    if (dispatchingOwnCancel || !g || e.pointerId !== g.id) return
    abandon()
  }

  const onClickCapture = (e: MouseEvent) => {
    if (!swallowNextClick) return
    swallowNextClick = false
    e.preventDefault()
    e.stopPropagation()
  }

  surface.addEventListener('pointerdown', onPointerDown)
  surface.addEventListener('pointermove', onPointerMove)
  surface.addEventListener('pointerup', onPointerUp)
  surface.addEventListener('pointercancel', onPointerCancel)
  surface.addEventListener('click', onClickCapture, true)
  return {
    onLocation,
    dropSnapshots,
    detach: () => {
      surface.removeEventListener('pointerdown', onPointerDown)
      surface.removeEventListener('pointermove', onPointerMove)
      surface.removeEventListener('pointerup', onPointerUp)
      surface.removeEventListener('pointercancel', onPointerCancel)
      surface.removeEventListener('click', onClickCapture, true)
      if (frame !== null) cancelFrame(frame)
      if (navFrame !== null) { cancelFrame(navFrame); navFrame = null; navQueue = [] }
      if (t) finish(t)
      springing?.done()
      dropSnapshots()
      themeObserver?.disconnect()
      window.removeEventListener('resize', dropSnapshots)
      window.removeEventListener('orientationchange', dropSnapshots)
      fonts?.removeEventListener?.('loadingdone', dropSnapshots)
      reducedMotion?.removeEventListener?.('change', dropSnapshots)
      const el = content()
      if (el) clearStyles(el)
    },
  }
}

/**
 * A static copy of the outgoing content, fixed exactly where it is on screen
 * (its current drag offset included), clipped to the scrolling surface so it
 * never covers the bottom navigation. Not interactive (`inert`, no pointer
 * events), hidden from assistive technology; once it has left it is either
 * kept, detached, as the stop's picture, or dropped.
 *
 * `offset` is the drag offset the region carries: what is on screen is judged
 * as if the region were at rest, so the copy is complete wherever it is shown
 * again — as a picture it rests `offset` further back.
 */
function makeGhost(region: HTMLElement, surface: HTMLElement, opacity: number, offset = 0): Ghost {
  const clip = surface.getBoundingClientRect()
  const rect = region.getBoundingClientRect()
  const frame = document.createElement('div')
  frame.setAttribute('aria-hidden', 'true')
  frame.setAttribute('inert', '')
  frame.dataset.swipeGhost = ''
  Object.assign(frame.style, {
    position: 'fixed', left: `${clip.left}px`, top: `${clip.top}px`,
    width: `${clip.width}px`, height: `${clip.height}px`,
    overflow: 'hidden', pointerEvents: 'none', zIndex: '15',
  })
  const clone = region.cloneNode(true) as HTMLElement
  // Form fields' current values are properties, not attributes: copy them so
  // a search field does not flash empty. Paired by position, so this comes
  // before the pruning below changes the clone's shape.
  const srcFields = region.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea')
  clone.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach((f, i) => {
    f.value = srcFields[i]?.value ?? f.value
  })
  // Only what is on screen needs to be in the clone. The first frame that shows
  // it moving must first paint it, on the main thread; a long list copied whole
  // cost that frame 75–117 ms at the 6× stand-in. Anything entirely outside the
  // visible area becomes an empty box of the same size, so what IS visible
  // lays out exactly as before. Ids must not be duplicated.
  // (A surface with no size has not been laid out: nothing can be judged off screen.)
  if (clip.width > 0 && clip.height > 0) pruneOffscreen(region, clone, clip, offset)
  // An image the original had not finished loading would be fetched again by
  // the copy: a picture sends no requests. (Loaded ones come from the
  // document's own image cache.) Paired by position, as the fields above.
  const srcImgs = region.querySelectorAll('img')
  clone.querySelectorAll('img').forEach((img, i) => {
    const src = srcImgs[i]
    if (src && !src.complete) { img.removeAttribute('src'); img.removeAttribute('srcset') }
  })
  clone.removeAttribute('id')
  clone.removeAttribute('data-swipe-region')
  clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'))
  clone.querySelectorAll('[data-swipe-region]').forEach((n) => n.removeAttribute('data-swipe-region'))
  Object.assign(clone.style, {
    position: 'absolute', left: `${rect.left - clip.left}px`, top: `${rect.top - clip.top}px`,
    width: `${rect.width}px`, margin: '0', transform: 'none', transition: 'none', opacity: String(opacity),
  })
  frame.appendChild(clone)
  document.body.appendChild(frame)
  return { frame, clone, x: 0, opacity, madeAt: offset }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function matches(query: string): boolean {
  return typeof window !== 'undefined' && window.matchMedia(query).matches
}

function prefersReducedMotion(): boolean {
  return matches('(prefers-reduced-motion: reduce)')
}

function requestFrame(cb: () => void): number {
  return typeof requestAnimationFrame === 'function' ? requestAnimationFrame(cb) : (cb(), 0)
}
function cancelFrame(id: number) {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id)
}

/**
 * In `clone` (a deep copy of `source`), replaces every element whose original
 * lies entirely outside `clip` with an empty box of the same size and margins,
 * so the visible part lays out exactly as in the original. Walks the two trees
 * in step; stops descending into anything that is replaced.
 */
function pruneOffscreen(source: Element, clone: Element, clip: DOMRect, offset = 0) {
  const src = Array.from(source.children)
  const cln = Array.from(clone.children)
  for (let i = 0; i < src.length && i < cln.length; i++) {
    const s = src[i] as HTMLElement
    const r = s.getBoundingClientRect()
    // Horizontally, where it is at rest (the drag offset taken out).
    const outside = r.bottom < clip.top || r.top > clip.bottom || r.right - offset < clip.left || r.left - offset > clip.right
    if (!outside) { pruneOffscreen(s, cln[i], clip, offset); continue }
    const cs = getComputedStyle(s)
    if (cs.position === 'fixed' || cs.position === 'absolute' || cs.display === 'inline' || cs.display === 'contents') continue
    const box = document.createElement('div')
    Object.assign(box.style, {
      width: `${r.width}px`, height: `${r.height}px`, margin: cs.margin, flex: '0 0 auto',
      boxSizing: 'border-box', visibility: 'hidden',
    })
    cln[i].replaceWith(box)
  }
}

/**
 * Where an element is on screen right now, mid-transition included: its
 * computed (animated) horizontal offset and opacity. `null` when unreadable.
 */
function animatedPlacement(el: HTMLElement): { x: number | null; opacity: number | null } {
  const cs = getComputedStyle(el)
  const opacity = cs.opacity === '' ? null : Number(cs.opacity)
  const t = cs.transform
  let x: number | null = null
  if (!t || t === 'none') x = 0
  else {
    const m3 = /^matrix3d\(([^)]+)\)$/.exec(t)
    const m2 = /^matrix\(([^)]+)\)$/.exec(t)
    const tr = /^translate3d\(\s*(-?[\d.]+)px/.exec(t)
    if (m3) x = Number(m3[1].split(',')[12])
    else if (m2) x = Number(m2[1].split(',')[4])
    else if (tr) x = Number(tr[1])
  }
  return { x: x !== null && Number.isFinite(x) ? x : null, opacity: opacity !== null && Number.isFinite(opacity) ? opacity : null }
}

/** Whether two stops are next to each other on the chain. */
function areNeighbours(a: ChainStop, b: ChainStop): boolean {
  return Math.abs(SWIPE_CHAIN.indexOf(a) - SWIPE_CHAIN.indexOf(b)) === 1
}

/** The distance between the content and its neighbour's picture: the page's width. */
function pageWidth(surface: HTMLElement): number {
  const w = surface.getBoundingClientRect().width
  return w > 0 ? w : window.innerWidth
}

/** Signed px/ms over the trailing `velocityWindowMs` (positive: the finger moving right). */
function signedVelocity(samples: { t: number; x: number }[]): number {
  if (samples.length < 2) return 0
  const last = samples[samples.length - 1]
  let first = samples[0]
  for (const s of samples) {
    if (last.t - s.t <= SWIPE_NAV.velocityWindowMs) { first = s; break }
  }
  const dt = last.t - first.t
  return dt > 0 ? (last.x - first.x) / dt : 0
}

/**
 * The settle of a pager drag: `distance` px still to go, starting at `speed`
 * px/ms toward the target (0 or less: from rest). The curve's initial slope is
 * the release speed in the curve's own units, so the motion continues the
 * flick instead of restarting; it then eases out. A slow release takes the
 * longest duration and starts gently; a flick is short and starts steep.
 */
export function settleMotion(distance: number, speed: number): { ms: number; easing: string } {
  const d = Math.max(1, Math.abs(distance))
  const v = Math.max(0, speed)
  const raw = v > 0 ? (SWIPE_NAV.settleSlope * d) / v : SWIPE_NAV.settleMaxMs
  const ms = Math.round(Math.min(SWIPE_NAV.settleMaxMs, Math.max(SWIPE_NAV.settleMinMs, raw)))
  const slope = (v * ms) / d
  const x1 = SWIPE_NAV.settleControlX1
  const y1 = Math.min(1, x1 * slope)
  return { ms, easing: `cubic-bezier(${x1}, ${Number(y1.toFixed(3))}, ${SWIPE_NAV.settleControlX2}, 1)` }
}

/** `done` once the transform transition of `el` has ended, or, failing the event, a little after it should have. */
function onTransformEnd(el: HTMLElement, ms: number, done: () => void) {
  let called = false
  const finish = () => {
    if (called) return
    called = true
    el.removeEventListener('transitionend', handler)
    el.removeEventListener('transitioncancel', handler)
    clearTimeout(timer)
    done()
  }
  const handler = (e: TransitionEvent) => {
    if (e.target === el && e.propertyName === 'transform') finish()
  }
  el.addEventListener('transitionend', handler)
  el.addEventListener('transitioncancel', handler)
  const timer = setTimeout(finish, ms + SWIPE_NAV.settleGraceMs)
}

/** The parts of a picture key every stop shares: how the page is drawn and by whom, and when. */
function environmentKey(
  surface: HTMLElement, s: SwipeState, epoch: number,
): Pick<SnapshotKey, 'theme' | 'viewport' | 'user' | 'locale' | 'clock' | 'epoch'> {
  const r = surface.getBoundingClientRect()
  const now = new Date()
  return {
    theme: document.documentElement.getAttribute('data-theme') ?? '',
    // The window, its pixel ratio, and the page area itself (a banner or the
    // keyboard moves or resizes it without resizing the window).
    viewport: [window.innerWidth, window.innerHeight, window.devicePixelRatio,
      Math.round(r.left), Math.round(r.top), Math.round(r.width), Math.round(r.height)].join(','),
    user: s.user,
    locale: s.locale,
    clock: `${now.toDateString()} ${now.getHours()}`,
    epoch,
  }
}

/** A stop's remembered list state (P4), as a comparable string. */
function memoryKey(stop: ChainStop): string {
  const m = readStopMemory()
  switch (stop) {
    // The segment is part of what Mine draws: a picture of Decks must not be
    // used for a Mine that would come back on Texts, or the reverse.
    case 'mine': return JSON.stringify([m.mine ?? null, readMineSegment()])
    case 'catalog-decks': return JSON.stringify(m.catalogDecks ?? null)
    case 'catalog-courses': return JSON.stringify(m.catalogCourses ?? null)
    case 'catalog-explore': return JSON.stringify(m.catalogExplore ?? null)
    default: return ''
  }
}

/** Whether anything inside `region` has a CSS animation (running, or held at its end). */
function hasCssAnimation(region: HTMLElement): boolean {
  if (typeof region.getAnimations !== 'function' || typeof CSSAnimation === 'undefined') return false
  return region.getAnimations({ subtree: true }).some((a) => a instanceof CSSAnimation)
}

function clearStyles(el: HTMLElement) {
  el.style.transition = ''
  el.style.transform = ''
  el.style.opacity = ''
}

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]'

function isEditable(el: Element): boolean {
  return el.closest(EDITABLE) !== null
}

function editableHasFocus(): boolean {
  const a = document.activeElement
  return a !== null && a !== document.body && a.matches(EDITABLE)
}

/** Decision 4.1: an element that declares it owns horizontal gestures. */
function optsOut(target: Element, surface: HTMLElement): boolean {
  for (let el: Element | null = target; el && el !== surface; el = el.parentElement) {
    if (el instanceof HTMLElement && el.dataset.swipeNav === 'off') return true
    const ta = getComputedStyle(el).touchAction
    if (ta && ta !== 'auto' && ta !== 'manipulation' && !/pan-x|pan-left|pan-right/.test(ta)) return true
  }
  return false
}

/**
 * Decision 4.1 as amended (first owner-decisions block): an inner horizontal
 * scroller wins only if it can still scroll in the swipe's direction right now.
 * A finger moving left (dx < 0) moves content toward a larger scrollLeft.
 */
function innerScrollerWins(target: Element, dx: number, surface: HTMLElement): boolean {
  for (let el: Element | null = target; el && el !== surface; el = el.parentElement) {
    const ox = getComputedStyle(el).overflowX
    if (ox !== 'auto' && ox !== 'scroll') continue
    if (el.scrollWidth <= el.clientWidth) continue
    const canMove = dx < 0
      ? el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      : el.scrollLeft > 0
    if (canMove) return true
  }
  return false
}

/** px/ms over the trailing `velocityWindowMs`. */
function velocity(samples: { t: number; x: number }[]): number {
  if (samples.length < 2) return 0
  const last = samples[samples.length - 1]
  let first = samples[0]
  for (const s of samples) {
    if (last.t - s.t <= SWIPE_NAV.velocityWindowMs) { first = s; break }
  }
  const dt = last.t - first.t
  return dt > 0 ? Math.abs(last.x - first.x) / dt : 0
}
